import flatpickr from 'flatpickr';
import 'flatpickr/dist/flatpickr.min.css';
import { Calendar } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { apiClient, PortalReservationEntry, portalApi } from '../lib/api';
import { isWhiteLabelOrigin } from '../lib/whiteLabel';
import { ApiError } from '../types';

const LS_KEY_PREFIX = 'portal_qr_';

function storageKey(hostCode: string | undefined) {
  return LS_KEY_PREFIX + (hostCode || window.location.host);
}

interface StoredReservations {
  reservations: PortalReservationEntry[];
}

// Reads + migrates the stored value. Legacy storage was a raw JWT string; new
// storage is a JSON list. Unparseable values fall back to a single-entry list
// so existing sessions aren't invalidated.
function readStored(key: string): PortalReservationEntry[] {
  const raw = localStorage.getItem(key);
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as StoredReservations;
    if (parsed && Array.isArray(parsed.reservations)) return parsed.reservations;
  } catch {
    // legacy: single token string
    if (typeof raw === 'string' && raw.length > 20) {
      return [{ token: raw, reservationId: 0, listingName: '', listingAddress: '' }];
    }
  }
  return [];
}

function writeStored(key: string, reservations: PortalReservationEntry[]) {
  localStorage.setItem(key, JSON.stringify({ reservations } satisfies StoredReservations));
}

type View =
  | { type: 'loading' }
  | { type: 'lookup' }
  | { type: 'picker'; reservations: PortalReservationEntry[] }
  | { type: 'error'; message: string };

// ─── Shared styles (mirrors ListingVerification for a consistent look) ─────
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '0.625rem 0.75rem',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  fontSize: '0.875rem',
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fff',
};

const dateInputWrapperStyle: React.CSSProperties = {
  position: 'relative',
  cursor: 'pointer',
};

const calendarIconStyle: React.CSSProperties = {
  position: 'absolute',
  right: '12px',
  top: '50%',
  transform: 'translateY(-50%)',
  color: '#9ca3af',
  pointerEvents: 'none',
};

const FLATPICKR_STYLE = `
  .portal-date-wrapper .flatpickr-input { display: none; }
  .portal-date-wrapper input.flatpickr-alt {
    width: 100%;
    padding: 0.625rem 2.5rem 0.625rem 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    font-size: 0.875rem;
    outline: none;
    box-sizing: border-box;
    background: #fff;
    cursor: pointer;
    color: #1f2937;
  }
  .portal-date-wrapper input.flatpickr-alt::placeholder {
    color: #9ca3af;
  }
  .portal-date-wrapper input.flatpickr-alt:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.15);
  }
`;

function pageShell(children: React.ReactNode) {
  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
          padding: '2rem',
          width: '100%',
          maxWidth: '440px',
        }}
      >
        {children}
      </div>
    </div>
  );
}

function fieldLabel(text: string) {
  return (
    <span
      style={{
        fontSize: '0.8125rem',
        fontWeight: 500,
        color: '#374151',
        display: 'block',
        marginBottom: '0.25rem',
      }}
    >
      {text}
    </span>
  );
}

function submitButtonStyle(disabled: boolean, loading: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: '0.75rem',
    background: disabled ? '#94a3b8' : '#0f172a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: 600,
    cursor: loading ? 'wait' : disabled ? 'not-allowed' : 'pointer',
    transition: 'background 0.15s',
  };
}

export default function PortalLogin() {
  const { t } = useTranslation('portal');
  const { hostCode } = useParams<{ hostCode?: string }>();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState<View>({ type: 'loading' });

  const key = storageKey(hostCode);

  const enterPortal = (entry: PortalReservationEntry) => {
    apiClient.setToken(entry.token);
    login(entry.token);
    navigate('/portal/home', { replace: true });
  };

  // On white-label origins the backend resolves the host from the request
  // Origin, so a missing hostCode param is valid. On bestystays.com we still
  // need the hostCode in the URL to pick the tenant.
  const hostResolvable = Boolean(hostCode) || isWhiteLabelOrigin();

  const missingHostView = (): View => ({
    type: 'error',
    message: t(
      'This portal link is missing host information. Please use the link or QR code your host gave you.'
    ),
  });

  // BES-10016: confirm the host code decodes on the backend before showing the
  // lookup form. Otherwise a bad link (e.g. a reservation code in the host slot)
  // lets the guest type their name and dates and then fails as if their details
  // were wrong. Only a 404 means the host is bad; other failures fall through to
  // the form so a transient API blip does not lock the guest out.
  const lookupViewAfterHostCheck = async (): Promise<View> => {
    if (!hostResolvable) return missingHostView();
    try {
      await portalApi.checkHost(hostCode ?? '');
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return missingHostView();
    }
    return { type: 'lookup' };
  };

  useEffect(() => {
    (async () => {
      const stored = readStored(key);
      if (stored.length === 0) {
        setView(await lookupViewAfterHostCheck());
        return;
      }

      // Validate each stored token; drop expired / invalid entries and
      // entries whose stay has already ended (departure in the past).
      const validated = await Promise.all(
        stored.map(async (entry) => {
          try {
            const status = await portalApi.status(entry.token);
            if (!status.tokenValid) return null;
            // Evict tokens for stays that have ended so the guest is prompted
            // to re-authenticate for their next reservation.
            if (status.departureTimestamp && new Date(status.departureTimestamp) < new Date()) {
              return null;
            }
            return entry;
          } catch {
            return null;
          }
        })
      );
      const live = validated.filter((e): e is PortalReservationEntry => e !== null);

      if (live.length === 0) {
        localStorage.removeItem(key);
        setView(await lookupViewAfterHostCheck());
        return;
      }

      if (live.length !== stored.length) writeStored(key, live);

      if (live.length === 1) {
        enterPortal(live[0]);
        return;
      }

      setView({ type: 'picker', reservations: live });
    })();
  }, [hostCode]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSingleMatched = (entry: PortalReservationEntry) => {
    writeStored(key, [entry]);
    enterPortal(entry);
  };

  const handleMultiMatched = (reservations: PortalReservationEntry[]) => {
    writeStored(key, reservations);
    setView({ type: 'picker', reservations });
  };

  if (view.type === 'loading') {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <p style={{ color: '#64748b' }}>{t('Loading...')}</p>
      </div>
    );
  }

  if (view.type === 'error') {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <p style={{ color: '#dc2626' }}>{view.message}</p>
      </div>
    );
  }

  if (view.type === 'picker') {
    return <PickerView reservations={view.reservations} onSelect={enterPortal} />;
  }

  // Host resolvability is enforced in the loading-state effect above; this
  // view is only reached once we know a hostCode param exists OR we're on a
  // white-label origin (backend resolves host from the Origin header).
  return (
    <LookupForm
      hostCode={hostCode}
      onSingleMatched={handleSingleMatched}
      onMultiMatched={handleMultiMatched}
    />
  );
}

function LookupForm(props: {
  hostCode: string | undefined;
  onSingleMatched: (entry: PortalReservationEntry) => void;
  onMultiMatched: (reservations: PortalReservationEntry[]) => void;
}) {
  const { t } = useTranslation('portal');
  const [guestName, setGuestName] = useState('');
  const [arrivalDate, setArrivalDate] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const arrivalRef = useRef<HTMLInputElement>(null);
  const departureRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = FLATPICKR_STYLE;
    document.head.appendChild(style);

    if (!arrivalRef.current || !departureRef.current) return;
    const fpConfig = (onChangeFn: (dateStr: string) => void): flatpickr.Options.Options => ({
      dateFormat: 'Y-m-d',
      altInput: true,
      altFormat: 'F j, Y',
      altInputClass: 'flatpickr-alt',
      disableMobile: true,
      onChange: (_dates: Date[], dateStr: string) => onChangeFn(dateStr),
    });
    const fpArrival = flatpickr(arrivalRef.current, fpConfig(setArrivalDate));
    const fpDeparture = flatpickr(departureRef.current, fpConfig(setDepartureDate));
    return () => {
      fpArrival.destroy();
      fpDeparture.destroy();
      document.head.removeChild(style);
    };
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!guestName || !arrivalDate || !departureDate) {
      setError(t('Please fill in all fields.'));
      return;
    }
    try {
      setLoading(true);
      const res = await portalApi.lookup({
        guestName: guestName.trim(),
        arrivalDate,
        departureDate,
        // Backend falls back to the request Origin for white-label hosts when
        // hostCode is empty, so sending '' is valid from a white-label domain.
        hostCode: props.hostCode ?? '',
      });
      if (res.status === 'no_match') {
        setError(t("We couldn't find a reservation matching those details."));
        return;
      }
      if (res.status === 'matched' && res.nonce) {
        const verify = await portalApi.verify({ nonce: res.nonce });
        if ('token' in verify) {
          props.onSingleMatched({
            token: verify.token,
            reservationId: verify.reservationId,
            listingName: '',
            listingAddress: '',
          });
        }
        return;
      }
      if (res.status === 'multi_match' && res.nonces) {
        const verify = await portalApi.verify({ nonces: res.nonces });
        if ('reservations' in verify) {
          props.onMultiMatched(verify.reservations);
        }
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        // Non-white-label hosts distribute /portal/<hostCode> links — a bare /portal
        // with no hostCode can't identify the host, so the lookup returns 404.
        setError(
          t(
            'This portal link is missing host information. Please use the link or QR code your host gave you.'
          )
        );
      } else {
        setError(t('Something went wrong. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  const disabled = loading || !guestName || !arrivalDate || !departureDate;

  return pageShell(
    <>
      <h1
        style={{
          fontSize: '1.25rem',
          fontWeight: 600,
          marginBottom: '0.25rem',
          textAlign: 'center',
        }}
      >
        {t('Find your reservation')}
      </h1>
      <p
        style={{
          color: '#64748b',
          fontSize: '0.875rem',
          textAlign: 'center',
          marginBottom: '1.5rem',
        }}
      >
        {t('Enter your details to access your guest portal')}
      </p>

      <form onSubmit={onSubmit}>
        <label style={{ display: 'block', marginBottom: '1rem' }}>
          {fieldLabel(t('Guest Name'))}
          <input
            type="text"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder={t('John Smith')}
            required
            style={inputStyle}
          />
        </label>

        <label style={{ display: 'block', marginBottom: '1rem' }}>
          {fieldLabel(t('Check-in Date'))}
          <div className="portal-date-wrapper" style={dateInputWrapperStyle}>
            <input ref={arrivalRef} type="text" placeholder={t('Select check-in date')} readOnly />
            <Calendar size={18} style={calendarIconStyle} />
          </div>
        </label>

        <label style={{ display: 'block', marginBottom: '1.25rem' }}>
          {fieldLabel(t('Check-out Date'))}
          <div className="portal-date-wrapper" style={dateInputWrapperStyle}>
            <input
              ref={departureRef}
              type="text"
              placeholder={t('Select check-out date')}
              readOnly
            />
            <Calendar size={18} style={calendarIconStyle} />
          </div>
        </label>

        {error && (
          <p
            style={{
              color: '#dc2626',
              fontSize: '0.8125rem',
              marginBottom: '1rem',
              textAlign: 'center',
            }}
          >
            {error}
          </p>
        )}

        <button type="submit" disabled={disabled} style={submitButtonStyle(disabled, loading)}>
          {loading ? t('Looking up…') : t('Continue')}
        </button>
      </form>
    </>
  );
}

function PickerView(props: {
  reservations: PortalReservationEntry[];
  onSelect: (entry: PortalReservationEntry) => void;
}) {
  const { t } = useTranslation('portal');
  return pageShell(
    <>
      <h1
        style={{
          fontSize: '1.25rem',
          fontWeight: 600,
          marginBottom: '0.25rem',
          textAlign: 'center',
        }}
      >
        {t('Choose your stay')}
      </h1>
      <p
        style={{
          color: '#64748b',
          fontSize: '0.875rem',
          textAlign: 'center',
          marginBottom: '1.5rem',
        }}
      >
        {t('You have multiple reservations for these dates.')}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
        {props.reservations.map((r) => (
          <button
            key={r.reservationId}
            onClick={() => props.onSelect(r)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '0.875rem 1rem',
              background: '#fff',
              border: '1px solid #d1d5db',
              borderRadius: '10px',
              cursor: 'pointer',
              transition: 'border-color 0.15s, background 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#0f172a';
              e.currentTarget.style.background = '#f8fafc';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.background = '#fff';
            }}
          >
            <div style={{ fontSize: '0.9375rem', fontWeight: 600, color: '#0f172a' }}>
              {r.listingName || t('Your stay')}
            </div>
            {r.listingAddress && (
              <div style={{ fontSize: '0.8125rem', color: '#64748b', marginTop: '0.125rem' }}>
                {r.listingAddress}
              </div>
            )}
          </button>
        ))}
      </div>
    </>
  );
}
